import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Subject, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ClinicService {
  private clinicsSubject = new BehaviorSubject<any[]>([]);
  public clinics$ = this.clinicsSubject.asObservable();

  private nursesSubject = new Subject<any[]>();
  public nurses$ = this.nursesSubject.asObservable();

  private headOfficeUsersSubject = new Subject<any[]>();
  public headOfficeUsers$ = this.headOfficeUsersSubject.asObservable();

  private clinicCreditSubject = new BehaviorSubject<number>(0);
  public clinicCredit$ = this.clinicCreditSubject.asObservable();

  constructor(private api: ApiService) {}

  // Method to update the clinic credit
  updateClinicCredit(credit: number) {
    this.clinicCreditSubject.next(credit);
  }

  // Method to update the clinics data
  updateClinics(clinics: any[]) {
    this.clinicsSubject.next(clinics);
  }

  // Method to update the nurses data
  updateNurses(nurses: any[]) {
    this.nursesSubject.next(nurses);
  }

  // Method to update the head office users data
  updateHeadOfficeUsers(users: any[]) {
    this.headOfficeUsersSubject.next(users);
  }

  getClinics(params?: any) {
    if (params) {
      params = {
        ...params,
      };
    }

    return this.api.getAPI('clinics', params);
  }

  createClinic(clinic: any) {
    return this.api.postAPI('clinics', clinic);
  }

  createNurse(nurse: any) {
    return this.api.postAPI('clinics/create-nurse', nurse);
  }

  getAdminNurses(queryParams?: any) {
    if (queryParams) {
      queryParams = {
        ...queryParams,
      };
    }
    return this.api.getAPI('clinics/clinic-admins', queryParams);
  }

  getHeadOfficeUsers(queryParams?: any) {
    if (queryParams) {
      queryParams = {
        ...queryParams,
      };
    }

    return this.api.getAPI('clinics/head-office-users', queryParams);
  }

  getHqUsers(queryParams?: any) {
    if (queryParams) {
      queryParams = {
        ...queryParams,
      };
    }
    
    return this.api.getAPI('clinics/hq-users', queryParams);
  }

  updateClinic(clinicId: any, clinicData: any) {
    return this.api.updateAPI(`clinics`, clinicId, clinicData);
  }

  assignNurseToClinic(clinic_data: any) {
    return this.api.postAPI(`clinics/clinic-nurse-assignment`, clinic_data);
  }

  getAssignedClinics(queryParams?: any) {
    if (queryParams) {
      queryParams = {
        ...queryParams,
      };
    }
    return this.api.getAPI('clinics/my-clinics', queryParams);
  }

  getNurses(clinicId?: any) {
    return this.api.getAPI(`clinics/clinic-nurses/${clinicId}`);
  }

  deleteClinic(clinicId: any) {
    return this.api.deleteAPI('clinics', clinicId);
  }

  exportClinicsToExcel() {
    return this.api.getFileAPI('clinics/export-clinic-data');
  }

  exportOrdersToExcel(queryParams?: any) {
    return this.api.getFileAPI(`clinics/export-order-data`, queryParams);
  }

  getCurrentNurse() {
    return this.api.getAPI('clinics/nurse/me');
  }

  updateNurse(nurseId: any, nurseData: any) {
    return this.api.updateAPI(`clinics/update-nurse`, nurseId, nurseData);
  }

  toggleClinic(clinicId: any) {
    return this.api.updateAPI(`clinics/toggle-clinic/${clinicId}`);
  }

  disableNurse(nurseId: any) {
    return this.api.updateAPI(`clinics/disable-nurse/${nurseId}`);
  }

  getAllClinicRoles() {
    return this.api.getAPI('clinics/clinic-roles');
  }

  getCinicCredit(clinicId: any) {
    return this.api.getAPI(`clinics/${clinicId}/credit`).pipe(
      tap((res: any) => {
        this.clinicCreditSubject.next(res.body.remaining_credit);
      })
    );
  }

  getNurseCount() {
    return this.api.getAPI(`clinics/nurse-count`);
  }

  getClinicCount() {
    return this.api.getAPI(`clinics/clinic-count`);
  }

  getHeadOfficeUserCount() {
    return this.api.getAPI(`clinics/hq-clinic-admin-count`);
  }
}
